import { useCallback } from 'react';
import { v4 as uuid } from 'uuid';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import { AxiosError } from 'axios';

import { NotificationApi } from '@api/NotificationApi';
import { EhrApi } from '@api/EhrApi';
import { translate } from '@chaika/i18n/helper';
import { store } from '@store';
import { queryClient } from '@store/queryClient';
import { recurringSubscriptionQueryOptions } from '@store/RecurringSubscription/queries/recurringSubscriptionQueries';
import { useCreateRecurringSubscription } from '@store/RecurringSubscription/queries/recurringSubscriptionMutations';
import { userSiteProfileQueryOptions } from '@user/queries/userSiteProfileQueries';
import { isTelegramUserActive } from '@user/utils/isTelegramUserActive';
import { userService } from '@user/services/userService';
import { DateFormat, DateHelper } from '@utils/DateHelper';
import { assertIsDefined } from '@utils/assertIsDefined';
import { RecurringSubscriptionStatus, SubscriptionOrderStatus } from 'chaika-enums';
import { ManualTemplateNames } from 'chaika-frontend-types/frontend-cms';
import { subscriptionOrderStore } from '@modules/patient/ui/EhrPage/pages/SubscriptionOrderPage/store/SubscriptionOrderStore';
import { isActivatedSubscriptionOrder } from '@patient/utils/isActivatedSubscriptionOrder';

const SELECTED_ORGANIZATION_MESSAGE = 'Patient messages are sent on behalf of the selected organization';
const DOCTOR_LOADED_MESSAGE = 'Doctor is loaded by loadIfNotExist before messages are sent';

interface SendContactParams {
    message: string;
    doctorId: string;
    fromEntry: boolean;
    recurringSubscriptionId: string;
    patientId?: string;
    billingAmount?: number;
    billingAmountDuration?: string;
    withForceSubscriptionCreation?: boolean;
}

interface PracticeRequestParams {
    message: string;
    practiceId: string;
    practiceName: string;
}

interface CreateTrialParams {
    doctorId: string;
    patientId: string;
}

interface CreateChatParams {
    doPatientHasChats: boolean;
    fromEntry: boolean;

    patientId: string;
    doctorId: string;

    message?: string;
    billingAmountData?: BillingAmountParams;
}

interface BillingAmountParams {
    billingAmount: number;
    billingAmountDuration: string;
}

interface CreateSubscriptionWithoutChatParams {
    patientId: string;
    doctorId: string;
    billingAmountData?: BillingAmountParams;
}

interface CreatePracticeChatParams {
    patientId: string;
    message: string;
    practiceId: string;
    practiceName: string;
}

interface Result {
    contactsSent: boolean;
    subscriptionCreated: boolean;
    hasErrors: boolean;
}

export interface UseEhrSubscription {
    createChatWithDoctor(params: CreateChatParams): Promise<void>;
    createPracticeChat(params: CreatePracticeChatParams): Promise<void>;
    sendDoctorContacts(params: SendContactParams): Promise<Result>;
    sendPracticeRequest(params: PracticeRequestParams): Promise<Result>;
    createTopic(params: Omit<SendContactParams, 'message' | 'fromEntry'>): Promise<Result>;
    createTrialSubscription(params: CreateTrialParams): Promise<void>;
    createSubscriptionWithoutChat(params: CreateSubscriptionWithoutChatParams): Promise<void>;
    doPatientHasActiveSubscriptions(patientId: string): Promise<boolean>;
    doPatientHasTrialSubscriptions(patientId: string): Promise<boolean>;
}

export function useEhrSubscription(): UseEhrSubscription {
    const createRecurringSubscriptionMutation = useCreateRecurringSubscription();

    const doPatientHasActiveSubscriptions = useCallback(async (patientId: string): Promise<boolean> => {
        const activeSubscriptionOrders = subscriptionOrderStore
            .getByPatientId(patientId)
            .filter(isActivatedSubscriptionOrder);

        const activeRecurringSubscriptions = await queryClient.ensureQueryData(
            recurringSubscriptionQueryOptions.list({ patientId, status: RecurringSubscriptionStatus.Active }),
        );

        return !isEmpty([...activeSubscriptionOrders, ...activeRecurringSubscriptions]);
    }, []);

    const doPatientHasTrialSubscriptions = useCallback(async (patientId: string): Promise<boolean> => {
        const trialSubscriptionOrders = subscriptionOrderStore
            .getByPatientId(patientId)
            .filter(subscriptionOrder => subscriptionOrder.status === SubscriptionOrderStatus.Active)
            .filter(subscriptionOrder => isNil(subscriptionOrder.paymentId));

        const activeRecurringSubscriptions = await queryClient.ensureQueryData(
            recurringSubscriptionQueryOptions.list({ patientId, status: RecurringSubscriptionStatus.Active }),
        );
        const trialRecurringSubscriptions = activeRecurringSubscriptions.filter(
            recurringSubscription => recurringSubscription.isTrial,
        );

        return !isEmpty([...trialSubscriptionOrders, ...trialRecurringSubscriptions]);
    }, []);

    const createChatWithDoctor = useCallback(
        async ({ doPatientHasChats, fromEntry, patientId, doctorId, message, billingAmountData }: CreateChatParams) => {
            const { userList, entry, authUser, organizationList } = store;

            await userService.loadIfNotExist([doctorId]);
            const siteProfile = await queryClient.fetchQuery(userSiteProfileQueryOptions.byUserId(doctorId));

            const doctor = userList.get(doctorId);

            if (!isTelegramUserActive(siteProfile)) {
                throw new Error(translate('needRegisterInTgBot'));
            }

            if (!doPatientHasChats) {
                const organizationId = fromEntry ? entry.organizationId : authUser.organizationId;
                assertIsDefined(organizationId, SELECTED_ORGANIZATION_MESSAGE);
                const organization = organizationList.getExisting(organizationId);
                const date = fromEntry ? entry.creationTime : new Date();
                assertIsDefined(doctor, DOCTOR_LOADED_MESSAGE);

                await NotificationApi.sendManualMessage(ManualTemplateNames.EntryDoctorContacts, {
                    organizationId,
                    entryId: fromEntry ? entry.id : undefined,
                    ehrIds: [patientId],
                    templateParams: {
                        date: DateHelper.isSameDay(new Date(), date)
                            ? translate('now').toLowerCase()
                            : DateHelper.format(date, DateFormat.DateWithMonthString),
                        organizationName: organization.name,
                        userFullName: doctor.shortFullName,
                        telegram: doctor.telegram,
                        userPhone: doctor.cityPhone,
                        subscriptionOrderChat: `https://chaika.com/chats/${patientId}/doctor/${doctor.id}`,
                        text: message,
                    },
                }).catch(() => {
                    throw new Error(translate('noAhealthContactTitle'));
                });
            }

            if (
                (await doPatientHasActiveSubscriptions(patientId)) ||
                (await doPatientHasTrialSubscriptions(patientId))
            ) {
                await EhrApi.createChatWithDoctor(patientId, doctorId);
            } else {
                await createRecurringSubscriptionMutation
                    .mutateAsync({
                        patientId,
                        doctorId,
                        buyerId: patientId,
                        recurringSubscriptionId: uuid(),
                        withTrial: true,
                        billingAmount: billingAmountData?.billingAmount,
                        billingAmountDuration: billingAmountData?.billingAmountDuration,
                    })
                    .catch((error?: AxiosError) => {
                        throw new Error(error?.message ?? 'Ну удалось создать подписку');
                    });
            }
        },
        [createRecurringSubscriptionMutation, doPatientHasActiveSubscriptions, doPatientHasTrialSubscriptions],
    );

    const createPracticeChat = useCallback(
        async ({ patientId, message, practiceId, practiceName }: CreatePracticeChatParams) => {
            const { authUser } = store;

            const siteProfile = await queryClient.fetchQuery(userSiteProfileQueryOptions.byUserId(authUser.id));

            if (!isTelegramUserActive(siteProfile)) {
                throw new Error(translate('needRegisterInTgBot'));
            }

            assertIsDefined(authUser.organizationId, SELECTED_ORGANIZATION_MESSAGE);
            await NotificationApi.sendManualMessage(ManualTemplateNames.PracticeChatRequest, {
                organizationId: authUser.organizationId,
                ehrIds: [patientId],
                templateParams: {
                    practiceName,
                    practiceChat: `https://chaika.com/chats/${patientId}/practice/${practiceId}`,
                    text: message,
                },
            }).catch(() => {
                throw new Error(translate('noAhealthContactTitle'));
            });
        },
        [],
    );

    const sendDoctorContacts = useCallback(
        async ({
            message,
            doctorId,
            fromEntry,
            patientId: targetPatientId,
            withForceSubscriptionCreation,
            ...params
        }: SendContactParams): Promise<Result> => {
            const { userList, authUser, ehr, organizationList, app, entry } = store;

            const patientId = targetPatientId ?? ehr.id;

            await userService.loadIfNotExist([doctorId]);
            const siteProfile = await queryClient.fetchQuery(userSiteProfileQueryOptions.byUserId(doctorId));
            const author = userList.get(doctorId);

            const result: Result = {
                contactsSent: false,
                subscriptionCreated: false,
                hasErrors: false,
            };

            const patientDoesntHaveActiveSubscriptions = !(await doPatientHasActiveSubscriptions(patientId));

            if (isTelegramUserActive(siteProfile)) {
                assertIsDefined(author, DOCTOR_LOADED_MESSAGE);
                const authorName = author.shortFullName;
                const organizationId = fromEntry ? entry.organizationId : authUser.organizationId;
                const date = fromEntry ? entry.creationTime : new Date();
                await app.doOrShowError({
                    action: async () => {
                        if (
                            (author.isDoctor || authUser.isDoctor) &&
                            (patientDoesntHaveActiveSubscriptions || withForceSubscriptionCreation)
                        ) {
                            await createRecurringSubscriptionMutation.mutateAsync({
                                patientId,
                                doctorId: author.id,
                                buyerId: patientId,
                                withTrial: true,
                                ...params,
                            });

                            result.subscriptionCreated = true;
                        }

                        assertIsDefined(organizationId, SELECTED_ORGANIZATION_MESSAGE);
                        await NotificationApi.sendManualMessage(ManualTemplateNames.EntryDoctorContacts, {
                            organizationId,
                            entryId: fromEntry ? entry.id : undefined,
                            ehrIds: [patientId],
                            templateParams: {
                                date: DateHelper.isSameDay(new Date(), date)
                                    ? translate('now').toLowerCase()
                                    : DateHelper.format(date, DateFormat.DateWithMonthString),
                                organizationName: organizationList.getExisting(organizationId).name,
                                userFullName: authorName,
                                telegram: author.telegram,
                                userPhone: author.cityPhone,
                                subscriptionOrderChat: `https://chaika.com/chats/${patientId}/doctor/${author.id}`,
                                text: message,
                            },
                        });

                        result.contactsSent = true;
                    },
                    onError: () => {
                        result.hasErrors = true;
                    },
                    errorMessage: translate('noAhealthContactTitle'),
                });
            } else {
                app.showingPopupParams = {
                    title: translate('noAhealthContactTitle'),
                    description: translate('needRegisterInTgBot'),
                };

                result.hasErrors = true;
            }

            return result;
        },
        [createRecurringSubscriptionMutation, doPatientHasActiveSubscriptions],
    );

    const sendPracticeRequest = useCallback(
        async ({ message, practiceId, practiceName }: PracticeRequestParams): Promise<Result> => {
            const { userList, authUser, ehr, app } = store;

            const authorId = authUser.id;
            await userService.loadIfNotExist([authorId]);
            const siteProfile = await queryClient.fetchQuery(userSiteProfileQueryOptions.byUserId(authorId));
            const author = userList.getExisting(authorId);

            const result: Result = {
                contactsSent: false,
                subscriptionCreated: false,
                hasErrors: false,
            };

            if (author.isAssistant || isTelegramUserActive(siteProfile)) {
                const organizationId = authUser.organizationId;
                assertIsDefined(organizationId, SELECTED_ORGANIZATION_MESSAGE);
                await app.doOrShowError({
                    action: async () => {
                        await NotificationApi.sendManualMessage(ManualTemplateNames.PracticeChatRequest, {
                            organizationId,
                            ehrIds: [ehr.id],
                            templateParams: {
                                practiceName,
                                practiceChat: `https://chaika.com/chats/${ehr.id}/practice/${practiceId}`,
                                text: message,
                            },
                        });

                        result.contactsSent = true;
                    },
                    onError: () => {
                        result.hasErrors = true;
                    },
                    errorMessage: translate('noAhealthContactTitle'),
                });
            } else {
                app.showingPopupParams = {
                    title: translate('noAhealthContactTitle'),
                    description: translate('needRegisterInTgBot'),
                };

                result.hasErrors = true;
            }

            return result;
        },
        [],
    );

    const createTopic = useCallback(
        async ({
            doctorId,
            patientId,
            ...params
        }: Omit<SendContactParams, 'message' | 'fromEntry'>): Promise<Result> => {
            const { userList, authUser, ehr, app } = store;

            await userService.loadIfNotExist([doctorId]);
            const siteProfile = await queryClient.fetchQuery(userSiteProfileQueryOptions.byUserId(doctorId));
            const author = userList.getExisting(doctorId);

            const result: Result = {
                contactsSent: false,
                subscriptionCreated: false,
                hasErrors: false,
            };

            const patientDoesntHaveActiveSubscriptions = !(await doPatientHasActiveSubscriptions(patientId ?? ehr.id));

            if (isTelegramUserActive(siteProfile)) {
                await app.doOrShowError({
                    action: async () => {
                        if ((author.isDoctor || authUser.isDoctor) && patientDoesntHaveActiveSubscriptions) {
                            await createRecurringSubscriptionMutation.mutateAsync({
                                patientId: patientId ?? ehr.id,
                                doctorId: author.id,
                                buyerId: patientId ?? ehr.id,
                                withTrial: true,
                                ...params,
                            });

                            result.subscriptionCreated = true;
                        }
                    },
                    errorMessage: translate('noAhealthContactTitle'),
                    onError: () => {
                        result.hasErrors = true;
                    },
                });

                result.contactsSent = true;
            } else {
                app.showingPopupParams = {
                    title: translate('noAhealthContactTitle'),
                    description: translate('needRegisterInTgBot'),
                };

                result.hasErrors = true;
            }

            return result;
        },
        [createRecurringSubscriptionMutation, doPatientHasActiveSubscriptions],
    );

    const createTrialSubscription = useCallback(
        async ({ patientId, doctorId }: CreateTrialParams) => {
            await createRecurringSubscriptionMutation.mutateAsync({
                patientId,
                doctorId,
                buyerId: patientId,
                withTrial: true,
                recurringSubscriptionId: uuid(),
            });
        },
        [createRecurringSubscriptionMutation],
    );

    const createSubscriptionWithoutChat = useCallback(
        async ({ patientId, doctorId, billingAmountData }: CreateSubscriptionWithoutChatParams) => {
            await createRecurringSubscriptionMutation.mutateAsync({
                patientId,
                doctorId,
                buyerId: patientId,
                recurringSubscriptionId: uuid(),
                withTrial: true,
                withoutChat: true,
                billingAmount: billingAmountData?.billingAmount,
                billingAmountDuration: billingAmountData?.billingAmountDuration,
            });
        },
        [createRecurringSubscriptionMutation],
    );

    return {
        createChatWithDoctor,
        createPracticeChat,
        sendDoctorContacts,
        sendPracticeRequest,
        createTopic,
        createTrialSubscription,
        createSubscriptionWithoutChat,
        doPatientHasActiveSubscriptions,
        doPatientHasTrialSubscriptions,
    };
}
